import { useEffect, useRef, useState } from "react";
import images from "../../utils/imageLoader";

const ProjectGallery = ({ project }) => {
    const gallery = project.gallery || [];
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const closeButtonRef = useRef(null);
    const lastTriggerRef = useRef(null);
    const modalRef = useRef(null);

    useEffect(() => {
        setActiveIndex(0);
        setIsExpanded(false);
    }, [project.id]);

    useEffect(() => {
        const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (gallery.length < 2 || isPaused || prefersReducedMotion) return undefined;

        const timer = window.setInterval(() => {
            setActiveIndex((current) => (current + 1) % gallery.length);
        }, 6000);

        return () => window.clearInterval(timer);
    }, [gallery.length, isPaused]);

    useEffect(() => {
        if (!isExpanded) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        window.requestAnimationFrame(() => closeButtonRef.current?.focus());

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                setIsExpanded(false);
                window.requestAnimationFrame(() => lastTriggerRef.current?.focus());
                return;
            }

            if (event.key === "Tab") {
                const focusable = Array.from(modalRef.current?.querySelectorAll("button, [href]") || []);
                const firstItem = focusable[0];
                const lastItem = focusable[focusable.length - 1];
                if (!firstItem || !lastItem) return;

                if (event.shiftKey && document.activeElement === firstItem) {
                    event.preventDefault();
                    lastItem.focus();
                } else if (!event.shiftKey && document.activeElement === lastItem) {
                    event.preventDefault();
                    firstItem.focus();
                }
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            document.body.style.overflow = previousOverflow;
        };
    }, [isExpanded]);

    if (!gallery.length) return null;

    const activeImage = gallery[activeIndex];
    const selectImage = (index) => {
        setActiveIndex(index);
        setIsPaused(true);
    };
    const move = (direction) => selectImage((activeIndex + direction + gallery.length) % gallery.length);
    const openExpanded = (event) => {
        lastTriggerRef.current = event.currentTarget;
        setIsExpanded(true);
    };
    const closeExpanded = () => {
        setIsExpanded(false);
        window.requestAnimationFrame(() => lastTriggerRef.current?.focus());
    };

    return (
        <section
            className="project-page__gallery"
            aria-labelledby="project-gallery-heading"
            onPointerEnter={() => setIsPaused(true)}
            onPointerLeave={() => setIsPaused(false)}
            onFocusCapture={() => setIsPaused(true)}
            onBlur={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) setIsPaused(false);
            }}
        >
            <div className="project-page__gallery-heading">
                <div>
                    <p className="project-page__eyebrow">INTERFACE GALLERY</p>
                    <h2 id="project-gallery-heading">See the product flow</h2>
                </div>
                <p>Selected screens from the current {project.readiness.toLowerCase()}.</p>
            </div>

            <div className="project-page__gallery-stage">
                <button
                    className="project-page__gallery-image-button"
                    type="button"
                    onClick={openExpanded}
                    aria-label={`Open larger image: ${activeImage.title}`}
                >
                    <img src={images[activeImage.image]} alt={activeImage.alt} />
                </button>
                <div className="project-page__gallery-caption">
                    <span aria-live="polite">{activeIndex + 1} / {gallery.length}</span>
                    <div>
                        <h3>{activeImage.title}</h3>
                        <p>{activeImage.caption}</p>
                    </div>
                    <button type="button" className="project-page__gallery-expand" onClick={openExpanded}>
                        Expand image ↗
                    </button>
                </div>
                {gallery.length > 1 && <div className="project-page__gallery-arrows">
                    <button type="button" onClick={() => move(-1)} aria-label={`Show previous image: ${gallery[(activeIndex - 1 + gallery.length) % gallery.length].title}`}>←</button>
                    <button type="button" onClick={() => move(1)} aria-label={`Show next image: ${gallery[(activeIndex + 1) % gallery.length].title}`}>→</button>
                </div>}
            </div>

            <div className="project-page__gallery-thumbnails" aria-label="Select an interface screen">
                {gallery.map((image, index) => (
                    <button
                        className={index === activeIndex ? "is-active" : ""}
                        type="button"
                        key={image.image}
                        onClick={() => selectImage(index)}
                        aria-current={index === activeIndex ? "true" : undefined}
                        aria-label={`Show image ${index + 1}: ${image.title}`}
                    >
                        <img src={images[image.image]} alt="" />
                        <span>{image.title}</span>
                    </button>
                ))}
            </div>

            {isExpanded && <div className="project-page__gallery-modal" role="dialog" aria-modal="true" aria-labelledby="project-gallery-modal-title" ref={modalRef} onMouseDown={(event) => { if (event.target === event.currentTarget) closeExpanded(); }}>
                <button className="project-page__gallery-modal-close" type="button" onClick={closeExpanded} aria-label="Close enlarged image" ref={closeButtonRef}>×</button>
                <div>
                    <img src={images[activeImage.image]} alt={activeImage.alt} />
                    <h2 id="project-gallery-modal-title">{activeImage.title}</h2>
                    <p>{activeImage.caption}</p>
                </div>
            </div>}
        </section>
    );
};

export default ProjectGallery;
